/**
 * Google Custom Search Adapter
 * Implementation for Google Custom Search JSON API
 * https://developers.google.com/custom-search/v1/overview
 */

import type { IWebSearchAdapter, WebSearchOptions } from './IWebSearchAdapter';
import type {
  GoogleSearchConfig,
  WebSearchResponse,
  WebSearchResult,
  RateLimitInfo,
} from '@/types/webSearch.types';
import { getBackendUrl, getAuthHeaders, prepareWebSearchQuery } from './webSearchUtils';

interface GoogleSearchItem {
  title?: string;
  link?: string;
  snippet?: string;
}

interface GoogleApiResponse {
  items?: GoogleSearchItem[];
  searchInformation?: {
    totalResults?: string;
  };
}

export class GoogleAdapter implements IWebSearchAdapter {
  public readonly provider = 'google';
  private config: GoogleSearchConfig | null = null;

  /**
   * Initialize the Google adapter with configuration
   */
  async initialize(config: GoogleSearchConfig): Promise<void> {
    if (!config.apiKey || config.apiKey.trim() === '') {
      throw new Error('Google API key is required');
    }

    if (!config.searchEngineId || config.searchEngineId.trim() === '') {
      throw new Error('Google Search Engine ID (CX) is required');
    }

    if (config.provider !== 'google') {
      throw new Error(`Invalid provider: expected 'google', got '${config.provider}'`);
    }

    this.config = config;
  }

  /**
   * Execute a web search using Google Custom Search API via backend proxy
   */
  async search(query: string, options?: WebSearchOptions): Promise<WebSearchResponse> {
    if (!this.config) {
      throw new Error('Adapter not initialized. Call initialize() first.');
    }

    if (!query || query.trim() === '') {
      throw new Error('Search query cannot be empty');
    }

    const startTime = Date.now();

    try {
      // Call backend proxy to avoid CORS issues
      const response = await fetch(`${getBackendUrl()}/web-search`, {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify({
          provider: 'google',
          query: prepareWebSearchQuery(query, 'google'),
          originalQueryLength: query.trim().length,
          config: {
            apiKey: this.config.apiKey,
            searchEngineId: this.config.searchEngineId,
            maxResults: this.config.maxResults || 10,
            dateRestrict: this.config.dateRestrict,
          },
        }),
        signal: options?.signal,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Google API error (${response.status}): ${errorText || response.statusText}`
        );
      }

      const data = await response.json() as GoogleApiResponse;

      // Normalize Google response to our standard format
      const items = data.items || [];
      const results: WebSearchResult[] = items.map((item: GoogleSearchItem) => ({
        title: item.title || '',
        url: item.link || '',
        snippet: item.snippet || '',
        publishedDate: undefined, // Google doesn't consistently provide publish dates
        relevanceScore: undefined, // Google doesn't provide relevance scores
      }));

      return {
        query,
        results,
        totalResults: parseInt(data.searchInformation?.totalResults || '0', 10),
        metadata: {
          provider: 'google',
          took: Date.now() - startTime,
          cached: false,
        },
      };
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Google search failed: ${error.message}`);
      }
      throw new Error('Google search failed with unknown error');
    }
  }

  /**
   * Test connection by performing a simple search
   */
  async testConnection(): Promise<boolean> {
    try {
      await this.search('test connection');
      return true;
    } catch (error) {
      console.error('Google connection test failed:', error);
      return false;
    }
  }

  /**
   * Google doesn't provide rate limit information in response
   */
  async getRateLimitInfo(): Promise<RateLimitInfo | null> {
    return null;
  }
}
